import { Fragment } from "react";

// Bare URL detector. Matches http/https URLs and stops at whitespace or a
// small set of characters that are almost never part of a real URL. Trailing
// punctuation (".,!?;:") is stripped after the match so "visit https://x.com."
// doesn't swallow the sentence period.
const URL_RE = /https?:\/\/[^\s<>"'`\])]+/g;
const TRAILING_PUNCT_RE = /[.,!?;:]+$/;

const LINK_CLASS =
  "text-daydra-accent/70 underline underline-offset-2 decoration-daydra-accent/20 hover:text-daydra-accent/90 hover:decoration-daydra-accent/40 transition-colors break-all";

/**
 * Split a plain string into text + URL segments. Unbalanced trailing
 * parentheses (common when the URL itself contains no `(`) are stripped so
 * "(see https://example.com/foo)" leaves the closing paren with the text.
 */
export function splitLinkify(
  text: string,
): Array<{ kind: "text"; value: string } | { kind: "url"; href: string; display: string }> {
  const out: Array<
    { kind: "text"; value: string } | { kind: "url"; href: string; display: string }
  > = [];
  let lastIndex = 0;
  // Reset state — URL_RE is global and stateful.
  URL_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = URL_RE.exec(text)) !== null) {
    const start = match.index;
    let raw = match[0];

    // Strip trailing sentence punctuation.
    const trailingMatch = raw.match(TRAILING_PUNCT_RE);
    let trailing = trailingMatch ? trailingMatch[0] : "";
    if (trailing) raw = raw.slice(0, -trailing.length);

    // Strip unbalanced trailing ')' — catches "(https://x.com/path)".
    while (raw.endsWith(")") && (raw.match(/\(/g) ?? []).length < (raw.match(/\)/g) ?? []).length) {
      trailing = ")" + trailing;
      raw = raw.slice(0, -1);
    }

    if (start > lastIndex) {
      out.push({ kind: "text", value: text.slice(lastIndex, start) });
    }
    out.push({ kind: "url", href: raw, display: raw });
    lastIndex = start + raw.length;
    if (trailing) {
      out.push({ kind: "text", value: trailing });
      lastIndex += trailing.length;
    }
  }
  if (lastIndex < text.length) {
    out.push({ kind: "text", value: text.slice(lastIndex) });
  }
  return out;
}

/**
 * Render a plain-text string with any bare URLs converted to clickable links
 * that open in a new tab. Preserves embedded newlines as <br />.
 */
export function Linkify({ text }: { text: string }) {
  const segments = splitLinkify(text);
  return (
    <>
      {segments.map((seg, i) => {
        if (seg.kind === "url") {
          return (
            <a
              key={i}
              href={seg.href}
              target="_blank"
              rel="noopener noreferrer"
              className={LINK_CLASS}
            >
              {seg.display}
            </a>
          );
        }
        if (!seg.value.includes("\n")) {
          return <Fragment key={i}>{seg.value}</Fragment>;
        }
        const lines = seg.value.split("\n");
        return (
          <Fragment key={i}>
            {lines.map((line, j) => (
              <Fragment key={j}>
                {line}
                {j < lines.length - 1 && <br />}
              </Fragment>
            ))}
          </Fragment>
        );
      })}
    </>
  );
}
